import type { ReactNode } from "react";

/** A phone, drawn in CSS: the lesson screen lives inside. */
export function PhoneFrame({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`relative mx-auto aspect-[9/19.2] w-[min(78vw,300px)] rounded-[46px] border border-white/15 bg-[#0B0A1C] p-[9px] shadow-[0_36px_80px_-30px_rgba(28,26,39,0.55),0_0_0_1px_rgba(0,0,0,0.6)] sm:w-[310px] ${className}`}
    >
      <div className="relative h-full w-full overflow-hidden rounded-[38px] bg-[#05061A]">
        {children}
        {/* Dynamic island */}
        <div aria-hidden className="absolute top-2.5 left-1/2 z-30 h-[22px] w-[84px] -translate-x-1/2 rounded-full bg-black" />
      </div>
    </div>
  );
}

/** The lesson's chrome over a full-bleed scene: beat progress, narration on a bottom fade, the quiet row, Continue. */
export function LessonChrome({
  beats,
  at,
  narration,
  onKnow,
  onAsk,
  onContinue,
  continueLabel = "Continue",
  askPressed,
  children,
}: {
  beats: number;
  at: number;
  narration: string;
  onKnow?: () => void;
  onAsk?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  askPressed?: boolean;
  children?: ReactNode;
}) {
  return (
    <>
      <div className="absolute inset-x-0 top-0 z-20 flex gap-1 px-4 pt-[42px]" aria-hidden>
        {Array.from({ length: beats }, (_, i) => (
          <div key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/20">
            <div className="h-full rounded-full bg-white transition-[width] duration-500" style={{ width: i <= at ? "100%" : "0%" }} />
          </div>
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-b from-transparent via-[rgba(5,6,26,0.86)] via-30% to-[#05061A] px-4 pt-16 pb-4">
        <p key={narration} className="la-fade-up min-h-[4.2em] text-[15px] leading-[1.38] font-extrabold text-white" aria-live="polite">
          {narration}
        </p>
        <div className="mt-3 flex items-center justify-between text-[12.5px] font-bold text-white/55">
          <button type="button" onClick={onKnow} className="py-1.5 pr-2 hover:text-white">
            I know this
          </button>
          <button type="button" onClick={onAsk} className={`rounded-lg py-1.5 pl-2 transition hover:text-white ${askPressed ? "text-white" : ""}`}>
            Ask a question
          </button>
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="mt-1.5 h-12 w-full rounded-2xl border-b-4 border-[#4128B8] bg-[#6B4EFF] text-[15px] font-black tracking-[0.04em] text-white uppercase transition hover:brightness-110 active:translate-y-[2px] active:border-b-2"
        >
          {continueLabel}
        </button>
      </div>
      {children}
    </>
  );
}

/**
 * A static photosynthesis scene, drawn in SVG: the fallback when the scene server isn't reachable,
 * and the backdrop for the tutor demo. `beat` picks what's lit (sun, CO2 in, sugar, chloroplasts).
 */
export function LeafScene({ beat = 0 }: { beat?: number }) {
  const on = (b: number) => ({ opacity: beat === b ? 1 : 0, transition: "opacity 600ms" });
  return (
    <svg viewBox="0 0 300 640" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full" aria-hidden>
      <defs>
        <linearGradient id="ls-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1B2A8C" />
          <stop offset="0.55" stopColor="#0D1450" />
          <stop offset="1" stopColor="#05061A" />
        </linearGradient>
        <radialGradient id="ls-sun" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FFF6D6" />
          <stop offset="0.4" stopColor="#FFD36E" />
          <stop offset="1" stopColor="#FF9E4D" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ls-leaf" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7BE08A" />
          <stop offset="1" stopColor="#2E9E62" />
        </linearGradient>
      </defs>
      <rect width="300" height="640" fill="url(#ls-sky)" />
      <circle cx="238" cy="118" r="70" fill="url(#ls-sun)" className="la-breathe" style={{ transformOrigin: "238px 118px" }} />
      <g style={on(0)}>
        {[0, 1, 2].map(i => (
          <line key={i} x1={210 - i * 18} y1={150 + i * 10} x2={168 - i * 26} y2={226 + i * 6} stroke="#FFE58A" strokeWidth="3" strokeLinecap="round" className="la-dash" />
        ))}
      </g>
      {/* The leaf */}
      <g transform="translate(150 300) rotate(-28)">
        <path d="M0 -120 C 70 -90, 86 30, 0 120 C -86 30, -70 -90, 0 -120 Z" fill="url(#ls-leaf)" />
        <path d="M0 -112 L0 150" stroke="#1F7A49" strokeWidth="4" strokeLinecap="round" />
        {[-70, -35, 0, 35, 70].map(y => (
          <g key={y} stroke="#1F7A49" strokeWidth="2.5" strokeLinecap="round" fill="none">
            <path d={`M0 ${y} Q 26 ${y - 18} 46 ${y - 30}`} />
            <path d={`M0 ${y} Q -26 ${y - 18} -46 ${y - 30}`} />
          </g>
        ))}
        <g style={on(1)}>
          {[
            [-30, 40],
            [28, -10],
            [-18, -60],
          ].map(([x, y]) => (
            <ellipse key={`${x}${y}`} cx={x} cy={y} rx="7" ry="4" fill="#0E3D25" stroke="#BDF5C8" strokeWidth="1.5" />
          ))}
        </g>
      </g>
      {/* CO2 drifting in */}
      <g style={on(1)}>
        {[
          [48, 210],
          [250, 380],
          [70, 430],
        ].map(([x, y], i) => (
          <g key={i} className="la-float" style={{ animationDelay: `${i * 0.7}s` }}>
            <circle cx={x} cy={y} r="9" fill="#9FE8FF" />
            <circle cx={x! - 14} cy={y} r="6" fill="#FF8A8A" />
            <circle cx={x! + 14} cy={y} r="6" fill="#FF8A8A" />
          </g>
        ))}
      </g>
      {/* Sugar: a six-carbon ring */}
      <g style={on(2)} transform="translate(150 300)">
        <polygon points="0,-34 30,-17 30,17 0,34 -30,17 -30,-17" fill="none" stroke="#FFF1C2" strokeWidth="4" className="la-glow" />
        {[0, 60, 120, 180, 240, 300].map(a => (
          <circle key={a} cx={34 * Math.sin((a * Math.PI) / 180)} cy={-34 * Math.cos((a * Math.PI) / 180)} r="6" fill="#FFD36E" />
        ))}
      </g>
      {/* Chloroplasts */}
      <g style={on(3)}>
        {[
          [110, 250],
          [180, 290],
          [130, 340],
          [200, 220],
        ].map(([x, y], i) => (
          <ellipse key={i} cx={x} cy={y} rx="20" ry="11" fill="#2E9E62" stroke="#BDF5C8" strokeWidth="2" className="la-float" style={{ animationDelay: `${i * 0.5}s` }} />
        ))}
      </g>
    </svg>
  );
}
